import React from 'react';
import { cn } from '../../shared/util/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'neutral' | 'outline-solid' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, disabled, children, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center rounded-full font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50 disabled:pointer-events-none';
    
    const variants = {
      primary: 'bg-primary text-white hover:opacity-95 hover:scale-[1.02] active:scale-[0.98] focus-visible:ring-primary shadow-[0_4px_14px_rgba(18,181,170,0.35)] dark:shadow-[0_4px_14px_rgba(18,181,170,0.25)]',
      secondary: 'bg-secondary text-white hover:opacity-95 hover:scale-[1.02] active:scale-[0.98] focus-visible:ring-secondary',
      neutral: 'bg-[var(--color-mockup-text-mid)] text-white hover:opacity-90 focus-visible:ring-[var(--color-mockup-text-mid)]',
      'outline-solid': 'border-2 border-[var(--color-mockup-border)] bg-transparent text-[var(--color-mockup-text)] hover:bg-black/5 dark:border-[var(--color-mockup-border)] dark:hover:bg-white/5 focus-visible:ring-[var(--color-mockup-text-mid)]',
      ghost: 'bg-transparent text-[var(--color-mockup-text-mid)] hover:bg-black/5 dark:hover:bg-white/5 focus-visible:ring-[var(--color-mockup-text-mid)]',
      danger: 'bg-[var(--color-mockup-coral)] text-white hover:opacity-95 focus-visible:ring-[var(--color-mockup-coral)]',
    };

    const sizes = {
      sm: 'h-8 px-4 text-xs',
      md: 'h-10 px-5 text-sm',
      lg: 'h-12 px-6 text-base',
    };

    return (
      <button
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
